import { useEffect, useRef, useState } from 'react';

function useScrollReveal(threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

const stats = [
  {
    metric: '74%',
    label: 'of employers admit to hiring the wrong person for a role',
    icon: 'ri-user-unfollow-line',
    source: 'CareerBuilder',
  },
  {
    metric: '5×',
    label: 'higher mis-hire risk without a consistent interview process',
    icon: 'ri-error-warning-line',
    source: 'SHRM Research',
  },
  {
    metric: '2×',
    label: 'annual salary — average cost of replacing a bad hire',
    icon: 'ri-money-dollar-circle-line',
    source: 'SHRM / Deloitte',
  },
];

const SALARY_PRESETS = [50000, 75000, 100000, 150000, 200000];
const HIRES_PRESETS = [5, 10, 20, 30, 50];

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
}

export default function MisHireSection() {
  const headerReveal = useScrollReveal();
  const statsReveal = useScrollReveal();
  const estimatorReveal = useScrollReveal();

  const [salary, setSalary] = useState(100000);
  const [hires, setHires] = useState(20);
  const [salaryInput, setSalaryInput] = useState('100,000');
  const [hiresInput, setHiresInput] = useState('20');

  const misHireRate = 0.3;
  const buildFitReduction = 0.8;
  const costPerMisHire = salary * 2;
  const annualExposure = Math.round(hires * misHireRate * costPerMisHire);
  const savingsWithBuildFit = Math.round(annualExposure * buildFitReduction);
  const remainingExposure = annualExposure - savingsWithBuildFit;
  const savingsPct = annualExposure > 0 ? Math.round((savingsWithBuildFit / annualExposure) * 100) : 0;

  function handleSalaryInput(val: string) {
    setSalaryInput(val);
    const numeric = parseInt(val.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(numeric) && numeric > 0) setSalary(numeric);
  }

  function handleHiresInput(val: string) {
    setHiresInput(val);
    const numeric = parseInt(val.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(numeric) && numeric > 0) setHires(numeric);
  }

  function handleSalaryBlur() { setSalaryInput(salary.toLocaleString()); }
  function handleHiresBlur() { setHiresInput(hires.toString()); }

  return (
    <section className="py-12 md:py-20 lg:py-24 border-t border-white/5">
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">

        {/* Header */}
        <div
          ref={headerReveal.ref}
          className={`mb-10 md:mb-14 transition-all duration-700 ${
            headerReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
            The Problem
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 lg:gap-6">
            <h2 className="text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight max-w-xl">
              Why Hiring Decisions Break Down
            </h2>
            <p className="text-white/40 text-sm leading-relaxed max-w-md">
              Many organizations rely on informal interviews where evaluation is inconsistent. This leads to subjective decisions, mis-hires, and unclear hiring outcomes.
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div
          ref={statsReveal.ref}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-10 md:mb-12"
        >
          {stats.map((stat, i) => (
            <div
              key={stat.metric}
              className={`relative bg-dark-card rounded-2xl p-5 sm:p-6 md:p-8 border border-white/5 overflow-hidden transition-all duration-700 ${
                statsReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-gold/8 rounded-full blur-2xl pointer-events-none"></div>
              <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gold/10 rounded-lg mb-4 sm:mb-5">
                <i className={`${stat.icon} text-gold text-base sm:text-lg`}></i>
              </div>
              <p className="text-white font-extrabold text-3xl sm:text-4xl md:text-5xl tracking-tight mb-2 sm:mb-3 leading-none">
                {stat.metric}
              </p>
              <p className="text-white/50 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                {stat.label}
              </p>
              <p className="text-white/20 text-[10px] font-medium uppercase tracking-wide">
                Source: {stat.source}
              </p>
            </div>
          ))}
        </div>

        {/* Mis-Hire Cost Estimator */}
        <div
          ref={estimatorReveal.ref}
          className={`transition-all duration-700 delay-200 ${
            estimatorReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="bg-dark-card rounded-2xl border border-white/5 overflow-hidden">
            {/* Estimator Header */}
            <div className="px-5 sm:px-7 md:px-10 py-5 sm:py-6 border-b border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                  <i className="ri-calculator-line text-gold text-base"></i>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm sm:text-base">Mis-Hire Cost Estimator</h3>
                  <p className="text-white/30 text-xs mt-0.5">Adjust inputs to see your organization&apos;s exposure</p>
                </div>
              </div>
              <span className="text-gold text-[10px] font-semibold tracking-[0.2em] uppercase bg-gold/10 px-3 py-1.5 rounded-full whitespace-nowrap self-start sm:self-auto">
                Interactive
              </span>
            </div>

            <div className="p-5 sm:p-7 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">

                {/* Left: Inputs */}
                <div className="space-y-6 sm:space-y-8">
                  {/* Salary Input */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-white/60 text-sm font-medium">Average Role Salary</label>
                      <div className="flex items-center gap-1.5 bg-dark-surface rounded-lg px-3 py-1.5 border border-white/8">
                        <span className="text-white/40 text-sm">$</span>
                        <input
                          type="text"
                          value={salaryInput}
                          onChange={(e) => handleSalaryInput(e.target.value)}
                          onBlur={handleSalaryBlur}
                          onFocus={() => setSalaryInput(salary.toString())}
                          className="bg-transparent text-white text-sm font-semibold w-20 sm:w-24 text-right outline-none"
                          aria-label="Average role salary"
                        />
                      </div>
                    </div>
                    <input
                      type="range"
                      min={30000}
                      max={500000}
                      step={5000}
                      value={salary}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setSalary(v);
                        setSalaryInput(v.toLocaleString());
                      }}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-gold bg-white/10"
                      aria-label="Salary slider"
                    />
                    <div className="flex gap-1.5 sm:gap-2 mt-3 flex-wrap">
                      {SALARY_PRESETS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => { setSalary(p); setSalaryInput(p.toLocaleString()); }}
                          className={`text-[10px] font-semibold px-2 sm:px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                            salary === p
                              ? 'border-gold text-gold bg-gold/10'
                              : 'border-white/10 text-white/30 hover:border-white/25 hover:text-white/50'
                          }`}
                        >
                          ${(p / 1000).toFixed(0)}K
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Hires Input */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-white/60 text-sm font-medium">Annual Hires</label>
                      <div className="flex items-center gap-1.5 bg-dark-surface rounded-lg px-3 py-1.5 border border-white/8">
                        <input
                          type="text"
                          value={hiresInput}
                          onChange={(e) => handleHiresInput(e.target.value)}
                          onBlur={handleHiresBlur}
                          onFocus={() => setHiresInput(hires.toString())}
                          className="bg-transparent text-white text-sm font-semibold w-12 sm:w-16 text-right outline-none"
                          aria-label="Annual hires"
                        />
                        <span className="text-white/40 text-xs">hires</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={100}
                      step={1}
                      value={hires}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setHires(v);
                        setHiresInput(v.toString());
                      }}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-gold bg-white/10"
                      aria-label="Hires slider"
                    />
                    <div className="flex gap-1.5 sm:gap-2 mt-3 flex-wrap">
                      {HIRES_PRESETS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => { setHires(p); setHiresInput(p.toString()); }}
                          className={`text-[10px] font-semibold px-2 sm:px-2.5 py-1 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                            hires === p
                              ? 'border-gold text-gold bg-gold/10'
                              : 'border-white/10 text-white/30 hover:border-white/25 hover:text-white/50'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="text-white/20 text-[10px] leading-relaxed">
                    Assumes 30% mis-hire rate without structured evaluation (SHRM). Cost per mis-hire = 2× annual salary. BuildFit AI reduces mis-hire rate by approximately 80% through structured evaluation frameworks.
                  </p>

                  {/* CTA */}
                  <a
                    href="https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-charcoal font-semibold text-sm px-6 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap"
                  >
                    Reduce Your Hiring Risk
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>

                {/* Right: Results */}
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
                    {/* Cost per hire */}
                    <div className="bg-dark-surface rounded-xl p-4 sm:p-5 border border-white/5">
                      <p className="text-white/40 text-[10px] sm:text-xs uppercase tracking-wide mb-1">Cost per mis-hire</p>
                      <p className="text-white font-extrabold text-2xl sm:text-3xl tracking-tight">
                        {formatCurrency(costPerMisHire)}
                      </p>
                      <p className="text-white/25 text-[10px] sm:text-xs mt-1">2× average role salary</p>
                    </div>

                    {/* Annual exposure */}
                    <div className="bg-dark-surface rounded-xl p-4 sm:p-5 border border-white/5">
                      <p className="text-white/40 text-[10px] sm:text-xs uppercase tracking-wide mb-1">Annual exposure</p>
                      <p className="text-white font-extrabold text-2xl sm:text-3xl tracking-tight">
                        {formatCurrency(annualExposure)}
                      </p>
                      <p className="text-white/25 text-[10px] sm:text-xs mt-1">
                        {Math.round(hires * misHireRate)} mis-hires × {formatCurrency(costPerMisHire)}
                      </p>
                    </div>
                  </div>

                  {/* Savings with BuildFit — highlighted */}
                  <div className="relative bg-gold/8 rounded-xl p-4 sm:p-5 border border-gold/20 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent pointer-events-none"></div>
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-gold text-xs uppercase tracking-wide font-semibold">Savings with BuildFit AI</p>
                        <span className="text-gold text-[10px] font-bold bg-gold/15 px-2 py-0.5 rounded-full">
                          {savingsPct}% reduction
                        </span>
                      </div>
                      <p className="text-white font-extrabold text-3xl sm:text-4xl tracking-tight">
                        {formatCurrency(savingsWithBuildFit)}
                      </p>
                      <p className="text-white/40 text-xs mt-1">
                        Remaining exposure: {formatCurrency(remainingExposure)}
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="bg-dark-surface rounded-xl p-4 sm:p-5 border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-white/40 text-xs uppercase tracking-wide">Risk Reduction</p>
                      <p className="text-gold text-xs font-semibold">{savingsPct}% saved</p>
                    </div>
                    <div className="w-full h-2 bg-white/8 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gold rounded-full transition-all duration-700"
                        style={{ width: `${savingsPct}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-white/20 text-[10px]">Without BuildFit AI</span>
                      <span className="text-white/20 text-[10px]">With BuildFit AI</span>
                    </div>
                  </div>


                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}